import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect, useRef } from 'react';

import type { HwcgActivationLogDto } from 'components/IncidentConfig/roster/activationLog/hwcgActivationLogModel';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import apiConfig from 'constants/apiConfig';
import { keys as rosterKeys } from 'hooks/incident/useRoster';
import { useHwcgFlag } from 'utils/featureFlags';

export const hwcgActivationLogKeys = {
  byIncident: (incidentId: string) =>
    [rosterKeys.hwcgActivationLog, incidentId] as const,
};

/**
 * The incident's HWCG activation log: every roster-seat and Group Actions
 * notice, per channel and per person, with the replies and the roster status
 * they produced. Polls, and refreshes the roster when a reply moved someone's
 * status so the table and the log never disagree.
 */
export function useHwcgActivationLog(
  incidentId?: string | null,
  options?: { enabled?: boolean }
) {
  const hwcg = useHwcgFlag();
  const actualIncidentId = useActualIncidentId();
  const resolvedIncidentId = incidentId ?? actualIncidentId ?? '';
  const queryClient = useQueryClient();

  const query = useQuery<HwcgActivationLogDto>({
    queryKey: hwcgActivationLogKeys.byIncident(resolvedIncidentId),
    enabled: hwcg && Boolean(resolvedIncidentId) && options?.enabled !== false,
    refetchInterval: 45_000,
    queryFn: async () => {
      const response = await axios.get(
        apiConfig.apiGateway.hwcgActivationLog(resolvedIncidentId)
      );
      return response.data;
    },
  });

  const lastCounts = useRef<string | null>(null);
  useEffect(() => {
    const summary = query.data?.summary;
    if (!summary) return;
    const counts = `${summary.activated}:${summary.declined}:${summary.pending}`;
    if (lastCounts.current !== null && lastCounts.current !== counts) {
      void queryClient.invalidateQueries({
        queryKey: [rosterKeys.incidentRoster, resolvedIncidentId],
      });
    }
    lastCounts.current = counts;
  }, [query.data, queryClient, resolvedIncidentId]);

  return query;
}
